import { Navigate, Route, Routes } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "@/app/store";
import DashboardLayout from "@/components/ui/common/DashboardLayout";
import AuthenticationPage from "@/components/admin/authentication/Login";
import Dashboard from "@/components/admin/dashboard";
import ProtectedRoute from "@/components/admin/AdminProtectedRoute";
import RolesAndAccess from "@/components/admin/roles";
import Loader from "@/components/Loader";
import IdleLogoutHandler from "@/components/IdleTimer";
import NotFoundPage from "@/components/PageNotFound";
import PrivacyStatement from "@/components/PrivacyStatement";
import TermsAndConditions from "@/components/TermsAndConditions";
import AllCounters from "@/components/admin/counter-management/Counter";
import StaffManagement from "@/components/admin/staff";
import ServiceManagement from "@/components/admin/service-management";
import LoginOTP from "@/components/admin/authentication/LoginOTP";
import LandingPage from "@/pages/landing-page/LandingPage";
import KisokHomePage from "@/pages/self-servives-kiosk/kiosk-home-page";
import OfferService from "@/pages/self-servives-kiosk/kiosk-service-page";
import Department from "@/components/admin/department";
import UserLogin from "@/components/remote-user/user-authentication/UserLogin";
import UserLoginOtp from "@/components/remote-user/user-authentication/UserLoginTopic";
import RegistrationForm from "@/components/remote-user/user-authentication/component/modal/RegisterationFormUser";
import DepartmentGrid from "@/components/admin/counter-management/live-queue-monitoring/DepartmentGrid";
import UserDashboardLayout from "@/components/ui/common/UserLayout";
import KioskDepartmentListing from "@/components/remote-user/user-portal/UserPortal";
import OfferServiceCard from "@/components/remote-user/user-portal/ServiceCard";
import RemoteUserProtectedRoute from "@/components/admin/UserProtectedRoute";
import ServeTicket from "@/components/admin/serve-ticket";
import { TicketProvider } from "@/context/TicketContext";
import UserForm from "@/components/remote-user/use-update-form/UserRegisterationForm";
import { WebSocketProvider } from "@/constants/websocket/Websocket";
import { EditProfile } from "@/components/remote-user/use-update-form/UserEditForm";
import UserTicketListing from "@/components/remote-user/ticket-listing";
import CheckMyTicketsListing from "./components/remote-user/check_my_tickets";
import UserTicketDetails from "@/components/remote-user/ticket-listing/components/UserTicketDetail";
import FeedbackForm from "@/components/remote-user/ticket-listing/components/UserFeedBackForm";
import WelcomePage from "@/components/admin/dashboard/WelcomePage";
import "leaflet/dist/leaflet.css";
import TaskManagement from "@/components/admin/tasks";
import TaskBoardView from "@/components/admin/kanban-view";
import TicketView from "@/components/admin/tickets-calendar";
import { WorkflowBuilder } from "./components/admin/workflow-builder/WorkflowBuilder";
import { WorkflowList } from "./components/admin/workflow-builder/WorkflowList";
import { WorkflowExecutionList } from "./components/admin/workflow-builder/WorkflowExecutionList";
import { WorkflowApprovals } from "./components/admin/workflow-builder/WorkflowApprovals";
import FormBuilder from "@/components/admin/form-builder";
import FormManagement from "./components/admin/forms";
import FormRenderer from "./components/admin/form-builder/renderer";
import FormViewer from "./components/admin/form-builder/viewer";
import { WorkflowSimulation } from "./components/admin/workflow-builder/Simulation";
import { FormSubmissionList } from "./components/admin/forms/FormSubmissionList";

function App() {

  const loading = useSelector((state: RootState) =>
    Object.values(state.api.queries).some(
      (query) =>
        query &&
        query.status === "pending" &&
        !["getAllTickets", "getAllTicketsByDepartment", "updateTask"].includes(
          query.endpointName
        )
    )
  );

  return (
    <>
      <IdleLogoutHandler />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/kiosk-home-page" element={<KisokHomePage />} />
        <Route path="/kiosk-service-offer/:id" element={<OfferService />} />
        <Route path="/login" index element={<AuthenticationPage />} />
        <Route path="/login-otp" index element={<LoginOTP />} />
        <Route path="/user-login" index element={<UserLogin />} />
        <Route path="/user-login-otp" index element={<UserLoginOtp />} />
        <Route path="/user-view" element={<Dashboard />} />
        <Route path="/privacy-statement" element={<PrivacyStatement />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
        <Route path="/check_my_tickets/:itsId" element={<CheckMyTicketsListing />} />
        <Route path="/form-submission/:id/:slug" element={<FormViewer />} />
        <Route path="/form/:slug" element={<FormRenderer />} />

        <Route
          path="/live-queue"
          element={
            <WebSocketProvider>
              <TicketProvider>
                <div className="bg-outletcolor h-screen ">
                  <DepartmentGrid />
                </div>
              </TicketProvider>
            </WebSocketProvider>
          }
        />

        <Route
          path="/live-queue/:start/:end"
          element={
            <WebSocketProvider>
              <TicketProvider>
                <div className="bg-outletcolor h-screen ">
                  <DepartmentGrid />
                </div>
              </TicketProvider>
            </WebSocketProvider>
          }
        />

        <Route path="/" element={<ProtectedRoute />}>
          <Route
            element={
              <WebSocketProvider>
                <TicketProvider>
                  <DashboardLayout />
                </TicketProvider>
              </WebSocketProvider>
            }
          >
            {/* <Route path="/admin/dashboard" element={<Dashboard />} /> */}
            <Route path="/admin/dashboard" element={<WelcomePage />} />
            <Route path="/admin/serve-ticket" element={<ServeTicket />} />
            {/* <Route path="/admin/tickets" element={<Tickets />} /> */}
            <Route path="/admin/tickets" element={<TicketView />} />
            <Route path="/admin/queue-monitor" element={<DepartmentGrid />} />
            <Route path="/admin/roles" element={<RolesAndAccess />} />
            <Route path="/admin/staff" element={<StaffManagement />} />
            <Route path="/admin/task" element={<TaskManagement />} />
            <Route path="/admin/kanbanview" element={<TaskBoardView />} />
            <Route path="/admin/service" element={<ServiceManagement />} />
            <Route path="/admin/counter" element={<AllCounters />} />
            <Route path="/admin/department" element={<Department />} />
            <Route path="/admin/workflow-builder" element={<WorkflowBuilder />} />
            <Route path="/admin/workflow-management" element={<WorkflowList />} />
            <Route path="/admin/workflow-executions" element={<WorkflowExecutionList />} />
            <Route path="/admin/workflow-approvals" element={<WorkflowApprovals />} />
            <Route path="/admin/workflow-simulation" element={<WorkflowSimulation />} />
            <Route path="/admin/form-builder" element={<FormBuilder />} />
            <Route path="/admin/form-management" element={<FormManagement />} />
            <Route path="/admin/form-submissions" element={<FormSubmissionList />} />
          </Route>
        </Route>
        <Route path="/" element={<RemoteUserProtectedRoute />}>
          <Route
            path="/user/register-user"
            index
            element={<RegistrationForm />}
          />
          <Route element={<UserDashboardLayout />}>
            <Route path="/user/home" element={<KioskDepartmentListing />} />
            <Route
              path="/user/edit-profile/:userId"
              element={<EditProfile />}
            />
            <Route path="/user/service/:id" element={<OfferServiceCard />} />
            <Route path="user/update-user" element={<UserForm />} />
            <Route
              path="/user/ticket-listing"
              element={<UserTicketListing />}
            />
            <Route
              path="/user/userticketdetail/:id/:name/:serviceId/:serviceName"
              element={<UserTicketDetails />}
            />
            <Route path="/user/feedback/:id" element={<FeedbackForm />} />
          </Route>
        </Route>
        <Route path="/notfound" element={<NotFoundPage />} />
        <Route path="*" element={<Navigate to="/notfound" replace />} />
      </Routes>
      <Loader open={loading} />
    </>
  );
}

export default App;
